var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __generator = (this && this.__generator) || function (thisArg, body) {
    var _ = { label: 0, sent: function() { if (t[0] & 1) throw t[1]; return t[1]; }, trys: [], ops: [] }, f, y, t, g = Object.create((typeof Iterator === "function" ? Iterator : Object).prototype);
    return g.next = verb(0), g["throw"] = verb(1), g["return"] = verb(2), typeof Symbol === "function" && (g[Symbol.iterator] = function() { return this; }), g;
    function verb(n) { return function (v) { return step([n, v]); }; }
    function step(op) {
        if (f) throw new TypeError("Generator is already executing.");
        while (g && (g = 0, op[0] && (_ = 0)), _) try {
            if (f = 1, y && (t = op[0] & 2 ? y["return"] : op[0] ? y["throw"] || ((t = y["return"]) && t.call(y), 0) : y.next) && !(t = t.call(y, op[1])).done) return t;
            if (y = 0, t) op = [op[0] & 2, t.value];
            switch (op[0]) {
                case 0: case 1: t = op; break;
                case 4: _.label++; return { value: op[1], done: false };
                case 5: _.label++; y = op[1]; op = [0]; continue;
                case 7: op = _.ops.pop(); _.trys.pop(); continue;
                default:
                    if (!(t = _.trys, t = t.length > 0 && t[t.length - 1]) && (op[0] === 6 || op[0] === 2)) { _ = 0; continue; }
                    if (op[0] === 3 && (!t || (op[1] > t[0] && op[1] < t[3]))) { _.label = op[1]; break; }
                    if (op[0] === 6 && _.label < t[1]) { _.label = t[1]; t = op; break; }
                    if (t && _.label < t[2]) { _.label = t[2]; _.ops.push(op); break; }
                    if (t[2]) _.ops.pop();
                    _.trys.pop(); continue;
            }
            op = body.call(thisArg, _);
        } catch (e) { op = [6, e]; y = 0; } finally { f = t = 0; }
        if (op[0] & 5) throw op[1]; return { value: op[0] ? op[1] : void 0, done: true };
    }
};
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
// SPDX-FileCopyrightText: 2026 Sequent Tech Inc <legal@sequentech.io>
//
// SPDX-License-Identifier: AGPL-3.0-only
import { useState } from "react";
import { useMutation } from "@apollo/client";
import { useTranslation } from "react-i18next";
import { Alert, Autocomplete, Button, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, TextField, Typography, } from "@mui/material";
import { IPermissions } from "@/types/keycloak";
import { EMessageAttemptState, EMessagePurpose, EMessagingProvider, MESSAGE_PURPOSES, } from "@/types/messaging";
import { recipientKind } from "@/services/messaging";
import { TEST_MESSAGING_ACCOUNT } from "@/queries/TestMessagingAccount";
/** An accepted message is not a delivered one: only DELIVERED reads as success. */
export var testStateSeverity = function (state) {
    switch (state) {
        case EMessageAttemptState.DELIVERED:
            return "success";
        case EMessageAttemptState.ACCEPTED:
        case EMessageAttemptState.QUEUED:
            return "info";
        case EMessageAttemptState.UNKNOWN:
            return "warning";
        case EMessageAttemptState.FAILED:
            return "error";
    }
};
/**
 * Whether a test names the approved template to send. WhatsApp needs one;
 * Infobip's Viber accounts list their own.
 */
export var testTemplateField = function (account, _purpose) {
    return account.provider === EMessagingProvider.WHATSAPP_CLOUD_API
        ? { offered: true, required: true }
        : { offered: false, required: false };
};
export var MessagingTestDialog = function (_a) {
    var _b;
    var account = _a.account, languages = _a.languages, onClose = _a.onClose;
    var t = useTranslation().t;
    var _c = useState(EMessagePurpose.OTP), purpose = _c[0], setPurpose = _c[1];
    var _d = useState(""), destination = _d[0], setDestination = _d[1];
    var _e = useState((_b = languages[0]) !== null && _b !== void 0 ? _b : ""), language = _e[0], setLanguage = _e[1];
    var _f = useState(""), template = _f[0], setTemplate = _f[1];
    var _g = testTemplateField(account, purpose), offersTemplate = _g.offered, needsTemplate = _g.required;
    var _h = useState(false), sending = _h[0], setSending = _h[1];
    var _j = useState(null), outcome = _j[0], setOutcome = _j[1];
    var testAccount = useMutation(TEST_MESSAGING_ACCOUNT, {
        context: { headers: { "x-hasura-role": IPermissions.MESSAGING_ACCOUNT_WRITE } },
    })[0];
    var send = function () { return __awaiter(void 0, void 0, void 0, function () {
        var data, result, _a;
        return __generator(this, function (_b) {
            switch (_b.label) {
                case 0:
                    setSending(true);
                    setOutcome(null);
                    _b.label = 1;
                case 1:
                    _b.trys.push([1, 3, 4, 5]);
                    return [4 /*yield*/, testAccount({
                            variables: __assign({ id: account.id, purpose: purpose, destination: destination.trim(), language: language.trim() || null }, (offersTemplate && template.trim() ? { template: template.trim() } : {})),
                        })];
                case 2:
                    data = (_b.sent()).data;
                    result = data === null || data === void 0 ? void 0 : data.test_messaging_account;
                    setOutcome(result
                        ? { kind: "RESULT", state: result.state, reason: result.reason }
                        : { kind: "ERROR" });
                    return [3 /*break*/, 5];
                case 3:
                    _a = _b.sent();
                    setOutcome({ kind: "ERROR" });
                    return [3 /*break*/, 5];
                case 4:
                    setSending(false);
                    return [7 /*endfinally*/];
                case 5: return [2 /*return*/];
            }
        });
    }); };
    return (_jsxs(Dialog, { open: true, onClose: onClose, fullWidth: true, maxWidth: "sm", "aria-labelledby": "messaging-test-title", children: [_jsx(DialogTitle, { id: "messaging-test-title", children: t("messagingAccounts.test.title", { name: account.name }) }), _jsxs(DialogContent, { sx: { display: "flex", flexDirection: "column", gap: 2, paddingTop: 1 }, children: [_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("messagingAccounts.test.description") }), _jsx(TextField, { select: true, label: t("messagingAccounts.test.purpose"), value: purpose, onChange: function (event) { return setPurpose(event.target.value); }, sx: { marginTop: 1 }, children: MESSAGE_PURPOSES.map(function (value) { return (_jsx(MenuItem, { value: value, children: t("messaging.purpose.".concat(value)) }, value)); }) }), _jsx(TextField, { label: t("messagingAccounts.test.destination.".concat(recipientKind(account.channel))), value: destination, onChange: function (event) { return setDestination(event.target.value); }, fullWidth: true }), _jsx(Autocomplete, { freeSolo: true, options: languages, inputValue: language, onInputChange: function (_event, value) { return setLanguage(value); }, renderInput: function (params) { return (_jsx(TextField, __assign({}, params, { label: t("messagingAccounts.test.language"), helperText: t("messagingAccounts.test.languageHelp") }))); } }), offersTemplate && (_jsx(TextField, { label: t("messagingAccounts.test.template"), helperText: t("messagingAccounts.test.templateHelp"), required: needsTemplate, value: template, onChange: function (event) { return setTemplate(event.target.value); }, fullWidth: true })), account.provider === EMessagingProvider.VIBER_INFOBIP && (_jsx(Typography, { variant: "body2", color: "text.secondary", children: t("messagingAccounts.test.viberTemplate") })), (outcome === null || outcome === void 0 ? void 0 : outcome.kind) === "RESULT" && (_jsxs(Alert, { severity: testStateSeverity(outcome.state), children: [_jsx("strong", { children: t("messaging.state.".concat(outcome.state)) }), ". ", t("messaging.stateHelp.".concat(outcome.state)), outcome.reason
                                ? " ".concat(t("messagingAccounts.test.reason", { reason: outcome.reason }))
                                : ""] })), (outcome === null || outcome === void 0 ? void 0 : outcome.kind) === "ERROR" && (_jsx(Alert, { severity: "error", children: t("messagingAccounts.test.error") }))] }), _jsxs(DialogActions, { children: [_jsx(Button, { variant: "outlined", onClick: onClose, children: t("messagingAccounts.editor.close") }), _jsx(Button, { variant: "contained", disabled: sending || !destination.trim() || (needsTemplate && !template.trim()), onClick: send, children: t("messagingAccounts.test.send") })] })] }));
};
